import { useCallback, useState } from 'react';
import { usePasteSpecial } from './excel/usePasteSpecial';
import { NAME_BOX_INPUT_ID } from './excel/NameBox';
import { deleteAxis, defaultDeleteInsertAxis, insertAxis, type Axis } from './excel/sheetActions';
import { getInternals } from './excel/workbookInternals';
import type { DeleteInsertMode, UseSheetEditorDialogsArgs, UseSheetEditorDialogsReturn } from './useSheetEditorDialogs.types';

export type { DeleteInsertMode, UseSheetEditorDialogsArgs, UseSheetEditorDialogsReturn } from './useSheetEditorDialogs.types';

/**
 * Dialog-open state for SheetEditorDrawer — kept out of the drawer's own
 * component body so its JSX stays composition-only. Selection/StatusBar/onOp
 * plumbing and the toolbar-item assembly live in their own colocated hooks
 * (useSheetSelectionStatus, useSheetToolbarItems) — neither is a dialog concern.
 */
export const useSheetEditorDialogs = ({
    open,
    workbookRef,
    historyOpen,
    closeFailedOpen,
    setCloseFailedOpen,
}: UseSheetEditorDialogsArgs): UseSheetEditorDialogsReturn => {
    const [goToOpen, setGoToOpen] = useState(false);
    const [formatCellsOpen, setFormatCellsOpen] = useState(false);
    // The header's "Edit name & description" dialog — owned here so it
    // shares this hook's own closeAnyDialog, instead of the drawer passing
    // its state in and out again.
    const [renameOpen, setRenameOpen] = useState(false);
    // Ctrl+-/Ctrl+Shift+= on a partial (not full-row/column) selection opens
    // the delete/insert dialog in the given mode.
    const [deleteInsertMode, setDeleteInsertMode] = useState<DeleteInsertMode>(null);
    const [deleteInsertAxis, setDeleteInsertAxis] = useState<Axis>('row');
    const openDeleteInsert = useCallback(
        (mode: 'delete' | 'insert') => {
            const wb = workbookRef.current;
            if (wb) setDeleteInsertAxis(defaultDeleteInsertAxis(wb));
            setDeleteInsertMode(mode);
        },
        [workbookRef],
    );

    const pasteSpecial = usePasteSpecial({ open, workbookRef, historyOpen });
    const { closeDialog } = pasteSpecial;

    const anyDialogOpen =
        renameOpen || goToOpen || formatCellsOpen || closeFailedOpen || deleteInsertMode !== null || pasteSpecial.dialogOpen;
    const closeAnyDialog = useCallback(() => {
        setRenameOpen(false);
        setGoToOpen(false);
        setFormatCellsOpen(false);
        // Esc/backdrop here is a retry-less dismiss — back to the editor
        // with the failed edit still pending, never a retry or a discard.
        setCloseFailedOpen(false);
        setDeleteInsertMode(null);
        closeDialog();
    }, [closeDialog, setCloseFailedOpen]);

    const isNameBoxTarget = useCallback(
        (target: EventTarget | null) => target instanceof HTMLElement && target.id === NAME_BOX_INPUT_ID,
        [],
    );

    const applyDeleteInsert = useCallback(() => {
        const wb = workbookRef.current;
        const internals = getInternals(wb);
        if (wb && internals && deleteInsertMode) {
            (deleteInsertMode === 'delete' ? deleteAxis : insertAxis)(wb, internals, deleteInsertAxis);
        }
        closeAnyDialog();
    }, [workbookRef, deleteInsertMode, deleteInsertAxis, closeAnyDialog]);

    return {
        goToOpen,
        setGoToOpen,
        formatCellsOpen,
        setFormatCellsOpen,
        deleteInsertMode,
        openDeleteInsert,
        deleteInsertAxis,
        setDeleteInsertAxis,
        renameOpen,
        setRenameOpen,
        pasteSpecial,
        anyDialogOpen,
        closeAnyDialog,
        isNameBoxTarget,
        applyDeleteInsert,
    };
};
